import React, { useState, useEffect } from 'react'
import BoldCheckMark from './BoldCheckMark'
import { useTap } from '../hooks/useTap'
import {
  acquireBottomImmersive,
  releaseBottomImmersive,
} from '../utils/flutterStatusBar'
import { SelectOption } from './CustomSelect'
import Gap from './Gap'
import { toastUtil } from '../utils/toast'
import { reviewApi } from '../api/review'
import { userApi } from '../api/user'

/**
 * 신고 사유 한 줄.
 *
 * 손 떼는 순간 처리한다(useTap). onClick 으로 두면 아이폰이 빠른 연속 탭을
 * 삼켜 고른 것이 안 바뀐다. 표식은 BoldCheckMark — 그림 두 장을 겹쳐 두고
 * opacity 로만 바꿔서, src 교체 때 생기던 '체크가 한 번 비어 보임'이 없다.
 */
const ReasonRow: React.FC<{
  label: string
  selected: boolean
  divider: boolean
  onSelect: () => void
}> = ({ label, selected, divider, onSelect }) => {
  const tap = useTap(onSelect)
  return (
    <button
      type="button"
      {...tap}
      className={`w-full py-12px flex items-center gap-8px text-start hover:opacity-70${
        divider ? ' border-t border-472410-10%' : ''
      }`}
      aria-pressed={selected}
    >
      <BoldCheckMark checked={selected} size={20} />
      <span
        className={`font-pretendard text-14px text-brown-80%-main ${
          selected ? 'font-700' : 'font-400'
        }`}
      >
        {label}
      </span>
    </button>
  )
}

interface ReportModalProps {
  isOpen: boolean
  /**
   * 신고할 기록. 사람을 신고할 때(reportUserId)는 넘기지 않는다.
   *
   * 둘 중 하나는 있어야 한다. 기록에서 열면 그 기록이, 프로필에서 열면
   * 그 사람이 신고 대상이다 — 서버도 통로가 둘로 나뉘어 있다.
   */
  reviewId?: number
  /** 신고할 사람. 프로필에서 열 때 쓴다. */
  reportUserId?: string
  onClose: () => void
}

const REPORT_REASONS: SelectOption[] = [
  { value: 'spam', label: '스팸 또는 광고성 내용' },
  { value: 'inappropriate', label: '부적절한 내용' },
  { value: 'harassment', label: '욕설 또는 비방' },
  { value: 'other', label: '기타' },
]

const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  reviewId,
  reportUserId,
  onClose,
}) => {
  // 센터 다이얼로그: 하단 SafeArea 해제 → dim이 홈 인디케이터까지 덮음
  useEffect(() => {
    if (!isOpen) return
    acquireBottomImmersive()
    return () => releaseBottomImmersive()
  }, [isOpen])

  const [selectedReason, setSelectedReason] = useState<SelectOption | null>(
    null
  )
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!selectedReason) {
      toastUtil.error('신고 사유를 선택해주세요')
      return
    }

    if (selectedReason.value === 'other' && !description.trim()) {
      toastUtil.error('상세 설명을 입력해주세요')
      return
    }

    setIsSubmitting(true)
    try {
      const reason = selectedReason.value as
        | 'spam'
        | 'inappropriate'
        | 'harassment'
        | 'other'
      const detail =
        selectedReason.value === 'other' ? description.trim() : null

      if (reportUserId) {
        await userApi.reportUser(reportUserId, {
          reason,
          description: detail,
        })
      } else if (reviewId !== undefined) {
        await reviewApi.report(reviewId, { reason, description: detail })
      }
      toastUtil.success('신고가 접수되었습니다')
      handleClose()
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || '신고 접수에 실패했습니다'
      toastUtil.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setSelectedReason(null)
    setDescription('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1050]"
        onClick={handleClose}
      />

      {/*
        모달 컨테이너.

        생김새를 ConfirmDialog 와 맞춘다 — 차단 팝업과 신고 팝업은 같은 ⋯ 시트에서
        나란히 열리는 형제라, 모서리 반경·너비·버튼 모양이 다르면 둘이 따로 논다.
        (rounded-12px / max-w-333px / h-44px 버튼)
      */}
      <div className="fixed inset-0 z-[1050] flex items-center justify-center px-18px">
        <div
          className={`
            w-full
            max-w-[333px]
            bg-white
            rounded-12px
            px-18px
            pt-24px
            pb-24px
            flex
            flex-col
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 제목 */}
          <p className="font-pretendard font-700 text-16px text-center text-brown-80%-main">
            {reportUserId ? '이 사용자를 신고하시겠어요?' : '이 기록을 신고하시겠어요?'}
          </p>

          <Gap height={16} />

          {/*
            사유를 펼쳐 둔다.

            예전에는 드롭다운이었다. 신고는 네 줄뿐인데 열고·고르고·닫는 세 번이
            들었고, 무엇을 고를 수 있는지 열기 전에는 보이지 않았다.
          */}
          <div className="w-full flex flex-col">
            {REPORT_REASONS.map((reason, index) => {
              const isSelected = selectedReason?.value === reason.value
              return (
                <ReasonRow
                  key={reason.value}
                  label={reason.label}
                  selected={isSelected}
                  divider={index > 0}
                  onSelect={() => setSelectedReason(reason)}
                />
              )
            })}
          </div>

          {/* 상세 설명 — '기타'를 골랐을 때만 */}
          {selectedReason?.value === 'other' && (
            <>
              <Gap height={12} />
              {/*
                공용 컴포넌트(TextAreaWithCounter)를 쓰다가 여기에 직접 넣었다.
                쓰는 곳이 이 팝업 하나뿐이었는데, 글자 수 카운터를 빼고 나니
                이름이 내용과 안 맞아서다.

                글자 크기는 14px — 위의 사유 네 줄과 같다. 공용 컴포넌트는
                16px(text-style-captionL)이라 고르는 항목보다 입력칸이 더
                커 보였다. 피드도 읽는 글은 전부 14px이다.
              */}
              <div
                className={`
                  flex
                  flex-col
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  focus-within:border-brandPrimary
                  p-12px
                `}
              >
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                  placeholder="어떤 점이 문제인지 알려주세요"
                  rows={3}
                  className={`
                    w-full
                    flex-1
                    p-0
                    font-pretendard
                    font-400
                    text-14px
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    placeholder:text-textColorText2
                    resize-none
                    scrollbar-hide
                    overflow-y-auto
                  `}
                />
              </div>
            </>
          )}

          <Gap height={24} />

          <div className="w-full flex gap-8px">
            <button
              type="button"
              onClick={handleClose}
              className={`
                flex-1
                h-44px
                rounded-12px
                bg-472410-10%
                font-pretendard
                font-bold
                text-16px
                text-brown-80%-main
                flex
                items-center
                justify-center
              `}
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!selectedReason || isSubmitting}
              className={`
                flex-1
                h-44px
                rounded-12px
                bg-brown-80%-main
                font-pretendard
                font-bold
                text-16px
                text-white
                flex
                items-center
                justify-center
                disabled:opacity-50
              `}
            >
              {isSubmitting ? '신고 중...' : '신고하기'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default ReportModal
